import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Link,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import { AuthProvider, useAuth } from './auth/AuthContext';
import './i18n/i18n';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './components/LanguageSwitcher';
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';
import PaymentWarningBanner from './components/PaymentWarningBanner';
import { useBillingStatus } from './hooks/useBilling';

// コード分割: 遅延読み込みコンポーネント
const AIDrivenCompetencyAssessmentTool = lazy(() => import('./AIDrivenCompetencyAssessmentTool'));
const CompetencyAdminPanel = lazy(() => import('./CompetencyAdminPanel'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));
// const AssessmentHistory = lazy(() => import('./AssessmentHistory')); // ユーザー画面から削除
const AssessmentDetailView = lazy(() => import('./AssessmentDetailView'));
const Login = lazy(() => import('./auth/Login'));
const Signup = lazy(() => import('./auth/Signup'));
const ResetPassword = lazy(() => import('./auth/ResetPassword'));
const ResetPasswordConfirmation = lazy(() => import('./auth/ResetPasswordDirect'));
const BillingManagement = lazy(() => import('./BillingManagement'));
const DepartmentManagement = lazy(() => import('./components/DepartmentManagement'));
const UserDetailView = lazy(() => import('./components/UserDetailView'));
const DepartmentManagementWrapper = lazy(() => import('./components/DepartmentManagementWrapper'));
const OrganizationChartWrapper = lazy(() => import('./components/OrganizationChartWrapper'));
const PerformanceCompetencyAnalysis = lazy(() => import('./components/PerformanceCompetencyAnalysis'));
// const WorkersAPITest = lazy(() => import('./test/WorkersAPITest'));
// const CompetencyAPIServiceIntegrationTest = lazy(() => import('./test/CompetencyAPIServiceIntegrationTest'));
// const IntegrationTestComponent = lazy(() => import('./IntegrationTestComponent'));
// const AuthDebugPage = lazy(() => import('./AuthDebugPage'));
const BillingSuccess = lazy(() => import('./BillingSuccess'));
const BillingCancel = lazy(() => import('./BillingCancel'));
const LandingPage = lazy(() => import('./LandingPage'));
const UserDashboard = lazy(() => import('./UserDashboard'));
const DemoAssessment = lazy(() => import('./components/DemoAssessment'));
// const LoginDebugPage = lazy(() => import('./LoginDebugPage'));

// React Query クライアントを作成
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5分
    },
  },
});

// ProtectedRouteは別ファイルからインポートして使用


// 管理画面用のナビゲーションコンポーネント
const AdminNavigation: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // 現在のページがどの管理画面かを判定
  const isAdminDashboard = location.pathname === '/admin/dashboard';
  const isAdminSettings = location.pathname === '/admin' || location.pathname === '/admin/settings';
  const isAdminPrices = location.pathname === '/prices';
  const isOrganizationChart = location.pathname === '/admin/organization';

  interface NavItem {
    to: string;
    label: string;
    isActive: boolean;
    description: string;
    icon: React.ReactNode;
  }

  const navItems: NavItem[] = [
    { 
      to: '/admin/dashboard', 
      label: t('admin.dashboard'), 
      isActive: isAdminDashboard,
      description: t('admin.dashboardDescription'),
      icon: (
        <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' />
        </svg>
      )
    },
    { 
      to: '/admin/departments', 
      label: '部署管理', 
      isActive: location.pathname === '/admin/departments',
      description: '組織の部署を管理します',
      icon: (
        <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' />
        </svg>
      )
    },
    { 
      to: '/admin/performance', 
      label: '個人業績管理', 
      isActive: location.pathname === '/admin/performance',
      description: '業績×コンピテンシー統合分析',
      icon: (
        <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' />
        </svg>
      )
    },
    { 
      to: '/admin/settings', 
      label: t('admin.settings'), 
      isActive: isAdminSettings,
      description: t('admin.settingsDescription'),
      icon: (
        <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
        </svg>
      )
    },
    { 
      to: '/prices', 
      label: t('billing.title'), 
      isActive: isAdminPrices,
      description: t('billing.description'),
      icon: (
        <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' />
        </svg>
      )
    },
  ];

  return (
    <div className='mb-6'>
      {/* パンくずリスト */}
      <div className='mb-4 text-sm text-gray-600'>
        <Link to='/' className='hover:text-gray-900'>{t('app.home')}</Link>
        <span className='mx-2'>/</span>
        <span className='text-gray-900 font-medium'>{t('admin.title')}</span>
      </div>
      
      {/* ナビゲーションカード */}
      <div className='grid grid-cols-2 md:grid-cols-5 gap-3'>
        {navItems.map((item, index) => (
          <button
            key={index}
            onClick={() => navigate(item.to)}
            className={`p-4 rounded-lg border transition-all duration-200 text-left ${
              item.isActive
                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300 shadow-md'
                : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
          >
            <div className='flex items-start space-x-3'>
              <div className={`p-2 rounded-lg ${
                item.isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {item.icon}
              </div>
              <div className='flex-1'>
                <div className={`font-medium ${
                  item.isActive ? 'text-blue-900' : 'text-gray-900'
                }`}>
                  {item.label}
                </div>
                <div className='text-xs text-gray-500 mt-0.5'>
                  {item.description}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ダッシュボードルーター（全ユーザーにマイページを表示）
const DashboardRouter: React.FC = () => {
  const { loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }
  
  // 全ユーザーにマイページ（UserDashboard）を表示
  return <UserDashboard />;
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, logout, loading } = useAuth();
  const navigate = useNavigate();
  
  // AuthContextから直接ロールを取得
  const role = currentUser?.role as string | null;
  const isAdminOrAbove = role === 'admin' || role === 'system_admin';


  const handleLogout = async () => {
    try {
      await logout();
      // ログアウト後、少し待ってからナビゲート（状態更新を確実にするため）
      setTimeout(() => {
        navigate('/');
      }, 100);
    } catch (error) {
      // エラーでもトップページへ
      navigate('/');
    }
  };

  interface NavItem {
    href: string;
    label: string;
    icon: React.ReactNode;
  }

  // ユーザーのロールに基づいてナビゲーション項目を生成
  const navItems: NavItem[] = [
    { 
      href: '#/dashboard', 
      label: 'マイページ', 
      icon: (
        <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' />
        </svg>
      )
    },
    { 
      href: '#/app', 
      label: t('app.start'), 
      icon: (
        <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
        </svg>
      )
    },
    ...(isAdminOrAbove ? [
      { 
        href: '#/admin/dashboard', 
        label: t('admin.title'), 
        icon: (
          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' />
          </svg>
        )
      },
    ] : []),
    ...(isAdminOrAbove ? [
      { 
        href: '#/admin/settings', 
        label: t('admin.settings'), 
        icon: (
          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
          </svg>
        )
      }
    ] : [])
  ];

  return (
    <header className='mb-2 bg-gradient-to-r from-blue-900 via-blue-800 to-teal-700 text-white p-3 md:p-6 fixed top-0 w-full z-50 backdrop-blur-md border-b border-white/10 shadow-2xl'>
      <div className='container mx-auto flex justify-between items-center'>
        <div className='flex items-center space-x-2 md:space-x-8'>
          <Link to='/' className='flex items-center space-x-2 md:space-x-3 hover:opacity-90 transition-opacity flex-shrink-0'>
            <div className='w-8 h-8 md:w-10 md:h-10 bg-white/15 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/20 shadow-lg'>
              <div className='w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-teal-200 to-blue-200 rounded-md flex items-center justify-center'>
                <svg className='w-3 h-3 md:w-4 md:h-4 text-blue-800' fill='currentColor' viewBox='0 0 24 24'>
                  <path d='M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5'/>
                </svg>
              </div>
            </div>
            <h1 className='text-sm md:text-xl font-bold bg-gradient-to-r from-white to-teal-200 bg-clip-text text-transparent whitespace-nowrap'>
              {t('app.title')}
            </h1>
          </Link>
          {currentUser && !loading && (
            <nav className='hidden lg:flex space-x-2'>
              {navItems.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  className='flex items-center space-x-1 px-3 py-2 rounded-xl text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300 backdrop-blur-sm border border-white/0 hover:border-white/20 hover:shadow-lg group'
                >
                  <div className='group-hover:scale-110 transition-transform duration-300 flex-shrink-0'>
                    {item.icon}
                  </div>
                  <span className='text-sm font-medium hidden xl:block'>{item.label}</span>
                </a>
              ))}
            </nav>
          )}
        </div>
        <div className='flex items-center space-x-2 md:space-x-4'>
          <div className='bg-white/10 rounded-xl p-1 md:p-2 backdrop-blur-sm border border-white/20'>
            <LanguageSwitcher />
          </div>
          {loading && (
            <div className='text-sm text-white/70'>認証中...</div>
          )}
          {currentUser && !loading && (
            <>
              <div className='flex items-center space-x-2 md:space-x-3 bg-white/10 rounded-xl px-2 md:px-4 py-1 md:py-2 backdrop-blur-sm border border-white/20'>
                <div className='w-6 h-6 md:w-8 md:h-8 bg-gradient-to-r from-teal-400 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-xs md:text-sm shadow-lg flex-shrink-0'>
                  {(currentUser.displayName || currentUser.email).charAt(0).toUpperCase()}
                </div>
                <div className='hidden lg:block'>
                  <div className='text-xs md:text-sm font-medium text-white/90 whitespace-nowrap'>
                    {currentUser.displayName || currentUser.email}
                  </div>
                  <div className='text-xs text-white/60'>
                    {role === 'system_admin' ? 'システム管理者' : role === 'admin' ? '管理者' : '一般ユーザー'}
                  </div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className='flex items-center space-x-1 px-3 py-2 rounded-xl text-white/90 hover:text-white hover:bg-red-600/20 transition-all duration-300 backdrop-blur-sm border border-white/20 hover:border-red-500/30 hover:shadow-lg group'
              >
                <svg className='w-4 h-4 group-hover:scale-110 transition-transform duration-300' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1' />
                </svg>
                <span className='text-sm font-medium hidden md:block'>ログアウト</span>
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const AppContent: React.FC = () => {
  const { t } = useTranslation();
  const { isPastDue } = useBillingStatus();
  const [showAssessment, setShowAssessment] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleStartClick = () => {
    setShowAssessment(true);
  };

  useEffect(() => {
    setShowAssessment(false);
  }, [location.pathname]);

  return (
    <div className='App'>
      <PaymentWarningBanner />
      <Header />
      <main className={`container mx-auto p-4 min-h-screen ${isPastDue ? 'mt-36' : 'mt-20'}`}>
        <Suspense fallback={<LoadingSpinner fullScreen text="読み込み中..." />}>
          <Routes>
          {/* パブリックルート（認証不要） */}
          <Route path='/' element={<LandingPage />} />
          <Route path='/demo' element={<DemoAssessment />} />

          {/* 認証関連ルート */}
          <Route
            path='/login'
            element={<Login onLoginSuccess={() => navigate('/dashboard')} />}
          />
          <Route
            path='/signup'
            element={
              <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
                <div className='bg-white p-8 rounded-lg shadow-md text-center max-w-md'>
                  <h2 className='text-2xl font-bold text-red-600 mb-4'>新規登録停止中</h2>
                  <p className='text-gray-600 mb-6'>
                    現在、新規アカウント登録は受け付けておりません。<br />
                    アカウントが必要な場合は、システム管理者にお問い合わせください。
                  </p>
                  <Link
                    to='/login'
                    className='inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors'
                  >
                    ログインページへ
                  </Link>
                </div>
              </div>
            }
          />
          <Route path='/reset-password' element={<ResetPassword />} />
          <Route path='/reset-password-confirmation' element={<ResetPasswordConfirmation />} />

          {/* プロテクテッドルート（認証必要） */}
          <Route
            path='/app'
            element={
              <ProtectedRoute>
                {!showAssessment ? (
                  <div className='min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 flex items-center justify-center relative overflow-hidden'>
                    {/* 背景装飾 */}
                    <div className='absolute inset-0 bg-gradient-to-br from-blue-100/20 via-teal-100/20 to-cyan-100/20'></div>
                    <div className='absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-blue-400/10 to-teal-400/10 rounded-full blur-3xl animate-pulse'></div>
                    <div className='absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-teal-400/10 to-cyan-400/10 rounded-full blur-3xl animate-pulse delay-1000'></div>
                    
                    <div className='relative z-10 text-center max-w-4xl mx-auto px-6'>
                      <div className='mb-8'>
                        <h1 className='text-3xl md:text-5xl font-bold bg-gradient-to-r from-blue-700 via-teal-600 to-cyan-700 bg-clip-text text-transparent mb-6 animate-fade-in'>
                          AI Feel Competency
                        </h1>
                        <p className='text-lg md:text-xl text-gray-600 mb-8 leading-relaxed animate-fade-in-delay'>
                          AIが11の領域であなたの能力を分析
                        </p>
                        <div className='flex flex-wrap justify-center gap-6 mb-12 text-gray-500'>
                          <div className='flex items-center space-x-2'>
                            <div className='w-3 h-3 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full'></div>
                            <span>AI分析システム</span>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <div className='w-3 h-3 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full'></div>
                            <span>リアルタイム評価</span>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <div className='w-3 h-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full'></div>
                            <span>個別最適化</span>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={handleStartClick}
                        className='group relative bg-gradient-to-r from-blue-700 via-teal-600 to-cyan-600 hover:from-blue-800 hover:via-teal-700 hover:to-cyan-700 text-white font-bold py-6 px-12 rounded-2xl text-xl md:text-2xl transition-all duration-500 transform hover:scale-105 hover:shadow-2xl shadow-xl border border-white/20 backdrop-blur-sm animate-bounce-subtle'
                      >
                        <div className='absolute inset-0 bg-gradient-to-r from-white/20 to-white/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300'></div>
                        <div className='relative flex items-center space-x-3'>
                          <svg className='w-6 h-6 group-hover:animate-pulse' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 10V3L4 14h7v7l9-11h-7z' />
                          </svg>
                          <span>{t('app.start')}</span>
                          <svg className='w-6 h-6 group-hover:translate-x-2 transition-transform duration-300' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7l5 5m0 0l-5 5m5-5H6' />
                          </svg>
                        </div>
                      </button>
                      
                      <div className='mt-8 text-sm text-gray-400'>
                        16領域の包括的な能力評価
                      </div>
                    </div>
                  </div>
                ) : (
                  <AIDrivenCompetencyAssessmentTool />
                )}
              </ProtectedRoute>
            }
          />

          {/* マイページ */}
          <Route
            path='/dashboard'
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />

          {/* 管理者ページへのリダイレクト */}
          <Route
            path='/admin'
            element={
              <ProtectedRoute requireAdmin={true}>
                <Navigate to='/admin/dashboard' replace />
              </ProtectedRoute>
            }
          />
          
          <Route
            path='/admin/settings'
            element={
              <ProtectedRoute requireAdmin={true}>
                <CompetencyAdminPanel />
              </ProtectedRoute>
            }
          />

          <Route
            path='/admin/dashboard'
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* 管理者ダッシュボードのタブごとのルート */}
          <Route
            path='/admin/dashboard/:tab'
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path='/admin/departments'
            element={
              <ProtectedRoute requireAdmin={true}>
                <DepartmentManagementWrapper />
              </ProtectedRoute>
            }
          />
          <Route
            path='/admin/performance'
            element={
              <ProtectedRoute requireAdmin={true}>
                <PerformanceCompetencyAnalysis />
              </ProtectedRoute>
            }
          />

          <Route
            path='/admin/assessment/:id'
            element={
              <ProtectedRoute requireAdmin={true}>
                <AssessmentDetailView />
              </ProtectedRoute>
            }
          />

          <Route
            path='/admin/organization'
            element={
              <ProtectedRoute requireAdmin={true}>
                <OrganizationChartWrapper />
              </ProtectedRoute>
            }
          />

          {/* ユーザーの評価履歴画面を削除
          <Route
            path='/history'
            element={
              <ProtectedRoute>
                <div className='min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-6'>
                  <AssessmentHistory />
                </div>
              </ProtectedRoute>
            }
          />
          */}

          {/* 新しく追加：個別評価結果詳細表示 */}
          <Route
            path='/assessment/:id'
            element={
              <ProtectedRoute>
                <AssessmentDetailView />
              </ProtectedRoute>
            }
          />

          <Route
            path='/prices'
            element={
              <ProtectedRoute>
                <BillingManagement />
              </ProtectedRoute>
            }
          />

          {/* <Route path='/test-api' element={<WorkersAPITest />} />

          <Route
            path='/test-integration'
            element={<CompetencyAPIServiceIntegrationTest />}
          />

          <Route path='/test-docker' element={<IntegrationTestComponent />} /> */}

          {/* <Route path='/debug-auth' element={<AuthDebugPage />} /> */}

          {/* <Route path='/debug-login' element={<LoginDebugPage />} /> */}

          <Route
            path="/billing/success"
            element={<BillingSuccessWithQuery />}
          />
          <Route path="/billing/cancel" element={<BillingCancel />} />

          <Route path='*' element={<Navigate to='/' replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  const [appInitialized, setAppInitialized] = useState(false);

  useEffect(() => {
    if (appInitialized || typeof window === 'undefined') return;
    
    setAppInitialized(true);

    // Supabaseの認証コールバック処理
    const handleAuthCallback = async () => {
      const url = new URL(window.location.href);
      const hashParams = new URLSearchParams(url.hash.substring(1));
      const queryParams = new URLSearchParams(url.search);
      
      // パラメータの確認
      const code = queryParams.get('code');
      const error = queryParams.get('error');
      const errorCode = queryParams.get('error_code');
      const errorDescription = queryParams.get('error_description');
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      

      // codeパラメータがある場合（パスワードリセットの新形式）
      if (code && window.location.hash.includes('reset-password-confirmation')) {
        // すでに正しいルートにいるので、Supabaseに処理させる
        // Supabaseが自動的にURLを処理できるようにする
        return;
      } else if (code && !error) {
        // codeがあってエラーがない場合、パスワードリセット確認画面へ
        window.location.href = `#/reset-password-confirmation?code=${code}`;
        return;
      }
      
      if (error) {
        // エラーの場合、パスワードリセット画面にリダイレクト
        
        // エラーメッセージをセッションストレージに保存
        if (errorCode === 'otp_expired') {
          sessionStorage.setItem('resetPasswordError', 'リセットリンクが期限切れです。もう一度パスワードリセットをリクエストしてください。');
        } else {
          sessionStorage.setItem('resetPasswordError', errorDescription || 'リセットリンクが無効です。');
        }
        
        // クエリパラメータをクリアしてリセット画面へ
        window.history.replaceState({}, document.title, window.location.pathname);
        window.location.hash = '/reset-password';
      } else if (type === 'recovery' && accessToken) {
        // パスワードリセット成功の場合（古い形式）、確認画面へ
        window.location.hash = `/reset-password-confirmation#${url.hash.substring(1)}`;
      } else if (accessToken && !type) {
        // 通常のログイン成功
        setTimeout(() => {
          if (window.location.hash.includes('access_token')) {
            window.location.hash = '/';
          }
        }, 1000);
      }
    };

    handleAuthCallback();
  }, [appInitialized]);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;

// クエリパラメータでisCardUpdateを判定してBillingSuccessに渡す
function BillingSuccessWithQuery() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isCardUpdate = params.get('cardUpdate') === '1';
  return <BillingSuccess isCardUpdate={isCardUpdate} />;
}